<template>
  <div class="enterprise-reports-center">
    <!-- 🏢 ENTERPRISE HEADER -->
    <div class="enterprise-header">
      <div class="header-background">
        <div class="header-overlay">
          <v-container class="pa-8">
            <v-row align="center">
              <v-col cols="12" md="8">
                <div class="brand-section">
                  <div class="brand-icon">
                    <v-icon size="56" color="white">mdi-chart-box-multiple-outline</v-icon>
                  </div>
                  <div class="brand-content">
                    <h1 class="display-2 font-weight-bold text-white mb-2">Centro de Reportes Empresarial</h1>
                    <!--
                      P13-V7B: the tagline promised "Inteligencia de negocios avanzada • Análisis en
                      tiempo real • Distribución automatizada". None of the three exists behind this
                      page; it now says what the page is.
                    -->
                    <p class="text-h6 text-white opacity-90">Acceso a los reportes con productor real</p>
                  </div>
                </div>
                
                <!--
                  P13-V5A-R5 · RETIRED. Las cuatro cifras de cabecera ("Reportes Hoy" 47,
                  "Usuarios Activos" 156, "Salud del Sistema" 98%, "Tiempo Respuesta" 2.1s) eran
                  literales sembrados en `liveData`, sobrescritos en el montaje por
                  Math.floor(Math.random() ...) bajo un setTimeout de 1 s rotulado "Simulate API
                  calls", y después movidos cada 30 s por un setInterval. Los chips de tendencia
                  ("+12%", "+23") y los estados "Excelente" y "Óptimo" eran literales fijos que
                  ninguna medición podía desmentir. El único productor montado bajo reportes es
                  /api/reports/organizational (backend src/app.js:568); no existe superficie
                  alguna que publique estas cuatro cifras. Una tarjeta sin productor se retira,
                  no se rellena.
                -->
                <div class="retired-metrics mt-6" data-testid="reports-center-live-metrics-retired">
                  <v-alert type="info" variant="tonal" density="compact">
                    Estas cifras no tienen un productor canónico y fueron retiradas en P13-V5A-R5.
                    Los indicadores organizacionales con respaldo real se publican en el reporte
                    organizacional (/reports/organizational).
                  </v-alert>
                </div>
              </v-col>
              
              <v-col cols="12" md="4">
                <!--
                  P13-V7B · RETIRED: "Generador Inteligente", "Programar Reporte" and "Hub de
                  Distribución". Each handler wrote a console.log and then raised a SUCCESS
                  notification saying the feature was "disponible próximamente": a green toast for
                  a capability that has no producer (no report generator, scheduler or distribution
                  surface is mounted in the backend). In their place, the report pages that do have
                  a producer — each offered only when its route admits this session.
                -->
                <div class="action-center" data-testid="reports-center-real-reports">
                  <v-btn
                    v-for="(link, index) in reportLinks"
                    :key="link.to"
                    :color="index === 0 ? 'white' : 'transparent'"
                    :size="index === 0 ? 'x-large' : 'large'"
                    :variant="index === 0 ? 'elevated' : 'outlined'"
                    class="mb-3 action-btn"
                    :class="{ secondary: index > 0 }"
                    :to="link.to"
                    :data-testid="`reports-center-link-${link.key}`"
                  >
                    <v-icon start>{{ link.icon }}</v-icon>
                    {{ link.label }}
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </div>
    </div>

    <!-- 🎛️ SMART NAVIGATION & FILTERS -->
    <v-container class="py-6">
      <v-card class="smart-nav-card mb-6" elevation="2">
        <v-card-text class="pa-6">
          <v-row align="center">
            <v-col cols="12" md="6">
              <div class="smart-tabs">
                <v-btn-toggle
                  v-model="activeSection"
                  color="primary"
                  variant="elevated"
                  divided
                  mandatory
                >
                  <v-btn value="dashboard" class="px-6">
                    <v-icon start>mdi-view-dashboard</v-icon>
                    Dashboard
                  </v-btn>
                  <v-btn value="catalog" class="px-6">
                    <v-icon start>mdi-folder-multiple</v-icon>
                    Catálogo
                  </v-btn>
                  <v-btn value="scheduled" class="px-6">
                    <v-icon start>mdi-calendar-clock</v-icon>
                    Programados
                  </v-btn>
                  <v-btn value="analytics" class="px-6">
                    <v-icon start>mdi-chart-line</v-icon>
                    Analytics
                  </v-btn>
                </v-btn-toggle>
              </div>
            </v-col>
            
            <!--
              P13-V7B · RETIRED: the "Buscar reportes, usuarios, o datos..." field. Its handler was
              `console.log('Smart search for:', query)`: typing searched nothing.
            -->
          </v-row>
        </v-card-text>
      </v-card>

      <!-- 📊 CONTENT SECTIONS -->
      <!--
        P13-V7B: the children no longer carry the listeners `@generate-report`, `@view-analytics`
        and `@preview-report`. Their only effect was an error toast saying the capability does not
        exist (and a jump to a tab of retirement notices); the controls that raised them are retired
        in ReportsDashboard.vue, and ReportsCatalog.vue emits nothing since P13-V5A-R5.
      -->
      <div v-show="activeSection === 'dashboard'">
        <ReportsDashboard />
      </div>

      <div v-show="activeSection === 'catalog'">
        <ReportsCatalog />
      </div>

      <div v-show="activeSection === 'scheduled'">
        <ScheduledReports />
      </div>

      <div v-show="activeSection === 'analytics'">
        <ReportsAnalytics />
      </div>
    </v-container>

    <!-- P13-V7B: the two notification snackbars are gone with the only controls that raised them. -->
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouteAdmission } from '@/composables/useRouteAdmission'

// Import Components
import ReportsDashboard from './components/ReportsDashboard.vue'
import ReportsCatalog from './components/ReportsCatalog.vue'
import ScheduledReports from './components/ScheduledReports.vue'
import ReportsAnalytics from './components/ReportsAnalytics.vue'

const { admits } = useRouteAdmission()

// Reactive Data
const activeSection = ref('dashboard')

/**
 * P13-V7B — the report pages that have a real producer, offered only when the router admits this
 * session (`/reports` itself admits super_admin and group_manager; `/reports/executive` does not
 * admit group_manager). Visibility only; the guard and the backend decide every navigation and read.
 */
const REPORT_LINK_CANDIDATES = Object.freeze([
  { key: 'organizational', label: 'Reportes Organizacionales', icon: 'mdi-sitemap', to: '/reports/organizational' },
  { key: 'executive', label: 'Reportes Ejecutivos', icon: 'mdi-briefcase-chart', to: '/reports/executive' }
])
const reportLinks = computed(() => REPORT_LINK_CANDIDATES.filter(link => admits(link.to)))

// P13-V5A-R5 · RETIRED. Aquí vivían `liveData` (los cuatro literales de cabecera),
// `loadInitialData()` (un setTimeout de 1 s rotulado "Simulate API calls" que los reemplazaba
// por Math.random), y el par `startLiveUpdates()`/`stopLiveUpdates()` con su setInterval de
// 30 s y `updateLiveMetrics()`, que hacía caminar las mismas cifras para que parecieran vivas.
// Ningún endpoint intervenía: este archivo no importa ningún cliente HTTP. Retirada la
// cabecera, no queda estado que inicializar ni temporizador que detener, por lo que también
// desaparecen el ciclo de vida y el prop `live-data` que se pasaba a ReportsDashboard (el hijo
// lo declaraba y nunca lo leía).

// P13-V7B · RETIRED with their controls: `performSmartSearch` (a console.log), the three "próximamente"
// openers (`openIntelligentGenerator`, `openScheduleCenter`, `openDistributionHub`) and the three
// child-event handlers of P13-V5A-R5 (`handleGenerateReport`, `handleAdvancedReportGeneration`,
// `handlePreviewReport`, via `notifyReportingUnavailable`). Those handlers had replaced a simulated
// success with a declared absence — correct — but the controls behind them still offered a report
// generation that could only answer "no disponible". The promise is retired at the control now.
</script>

<style scoped lang="scss">
.enterprise-reports-center {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.enterprise-header {
  position: relative;
  overflow: hidden;
}

.header-background {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #1e3c72 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.3;
  }
}

.header-overlay {
  position: relative;
  z-index: 1;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.05);
}

.brand-section {
  display: flex;
  align-items: center;
  gap: 24px;
}

.brand-icon {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.retired-metrics {
  margin-top: 32px;
  max-width: 720px;
}

.action-center {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.action-btn {
  border-radius: 12px !important;
  
  &.secondary {
    color: white !important;
    border-color: rgba(255, 255, 255, 0.3) !important;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1) !important;
    }
  }
}

.smart-nav-card {
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
}

.smart-tabs {
  .v-btn-toggle {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }
}

.search-field {
  :deep(.v-field) {
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
  }
}
</style>
